import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import SearchManager from 'flarum/common/SearchManager';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';
import AiStream from './ai/AiStream';
import { aiAvailable } from './ai/availability';
import { citationLink, discussionUrl, postUrl, renderCitedText, sourceTitle } from './ai/citations';
import isVnodeWithClass from './utils/isVnodeWithClass';

// SearchModal is a lazy chunk, so it is extended by module path.
const MODAL = 'flarum/common/components/SearchModal';
const SUMMARY_RESOURCES = ['discussions', 'posts'];
const SUMMARY_MIN_RESULTS = 2;
const SUMMARY_DEBOUNCE_MS = 400;

// The protected SearchModal members this reads.

const internals = modal => modal;
class ModalAiState {
  constructor() {
    _defineProperty(this, "summary", new AiStream('summary-stream', true));
    _defineProperty(this, "ask", new AiStream('ask-stream', false));
    _defineProperty(this, "askMode", false);
    _defineProperty(this, "summaryExpanded", false);
    _defineProperty(this, "scheduledKey", null);
    _defineProperty(this, "queryObserver", null);
    _defineProperty(this, "timer", null);
  }
  schedule(key, run) {
    this.unschedule();
    this.scheduledKey = key;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.scheduledKey = null;
      run();
    }, SUMMARY_DEBOUNCE_MS);
  }
  unschedule() {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.scheduledKey = null;
  }
  dispose() {
    var _this$queryObserver;
    this.unschedule();
    this.summary.cancel();
    this.ask.cancel();
    (_this$queryObserver = this.queryObserver) == null || _this$queryObserver.end(true);
  }
}

/**
 * The AI summary above the Discussions/Posts results and the "Ask AI" mode of
 * the SearchModal. Only for logged-in users while the backend has a chat key.
 */
export function bootAiModal() {
  const features = {
    summary: !!app.forum.attribute('glowingblueSearchSummaryEnabled'),
    ask: !!app.forum.attribute('glowingblueSearchAskEnabled')
  };
  if (!features.summary && !features.ask) return;
  if (!app.forum.attribute('glowingblueSearchChatAvailable')) return;
  const states = new WeakMap();
  extend(MODAL, 'oninit', function () {
    const state = new ModalAiState();
    states.set(this, state);

    // Esc clears the query without a redraw, so watch the stream itself.
    state.queryObserver = internals(this).query.map(() => {
      if (sync(this, state, features)) m.redraw();
    });
  });
  extend(MODAL, 'oncreate', function () {
    var _internals$inputEleme;
    const state = states.get(this);
    if (!state) return;

    // Core's own Enter handler selects a result, which in Ask mode is a no-op.
    (_internals$inputEleme = internals(this).inputElement()[0]) == null || _internals$inputEleme.addEventListener('keydown', e => {
      if (e.key !== 'Enter' || e.isComposing || !features.ask || !state.askMode || !aiAvailable()) return;
      submitAsk(this, state);
    });
    if (sync(this, state, features)) m.redraw();
  });
  extend(MODAL, 'onupdate', function () {
    const state = states.get(this);
    if (state && sync(this, state, features)) m.redraw();
  });
  extend(MODAL, 'onremove', function () {
    var _states$get;
    (_states$get = states.get(this)) == null || _states$get.dispose();
  });
  if (features.summary) {
    extend(MODAL, 'activeTabItems', function (items) {
      const state = states.get(this);
      if (!state || state.askMode || !aiAvailable()) return;
      const target = summaryTarget(this);
      if (!target || target === 'unsupported') return;
      const readiness = resultsReadiness(this, target.raw);
      if (readiness === 'few') return;
      if (readiness === 'pending' && !state.summary.text && !summaryMessage(state.summary.status)) return;
      items.add('glowingblueSummary', renderSummary(state, target), 100);
    });
  }
  if (features.ask) {
    extend(MODAL, 'content', function (vnode) {
      const state = states.get(this);
      const children = vnode.children;
      if (!state || !aiAvailable() || !Array.isArray(children)) return;
      const formIndex = children.findIndex(child => isVnodeWithClass(child, 'SearchModal-form'));
      children.splice(formIndex + 1, 0, renderAskToggle(this, state));
      if (!state.askMode) return;
      if (formIndex >= 0) setAskPlaceholder(children[formIndex]);
      const tabsIndex = children.findIndex(child => isVnodeWithClass(child, 'Tabs'));
      const panel = renderAskPanel(this, state);
      if (tabsIndex >= 0) children.splice(tabsIndex, 1, panel);else children.push(panel);
    });
  }
}

/**
 * Bring the AI requests in line with the modal's query, tab and results.
 * Runs after every redraw and on every query change; returns whether
 * anything visible changed.
 */
function sync(modal, state, features) {
  if (!aiAvailable()) {
    const wasAsking = state.askMode;
    state.askMode = false;
    state.unschedule();
    const summaryChanged = state.summary.reset();
    const askChanged = state.ask.reset();
    return wasAsking || summaryChanged || askChanged;
  }
  let changed = false;
  if (features.ask && !internals(modal).query().trim()) {
    changed = state.ask.reset() || changed;
  }
  if (features.summary) {
    changed = syncSummary(modal, state) || changed;
  }
  return changed;
}
function syncSummary(modal, state) {
  const summary = state.summary;
  if (state.askMode) {
    state.unschedule();
    return summary.cancel();
  }
  const target = summaryTarget(modal);
  if (target === 'unsupported') {
    state.unschedule();
    return false;
  }
  if (!target) {
    state.unschedule();
    return summary.reset();
  }
  const readiness = resultsReadiness(modal, target.raw);
  if (readiness === 'few') {
    state.unschedule();
    return summary.reset();
  }
  if (readiness === 'pending') {
    if (state.scheduledKey !== target.key) state.unschedule();
    return summary.key !== target.key ? summary.cancel() : false;
  }
  if (summary.key === target.key && summary.status !== 'aborted') return false;
  if (state.scheduledKey === target.key) return false;
  const changed = summary.cancel();
  state.schedule(target.key, () => {
    state.summaryExpanded = false;
    summary.start(target.key, target.request);
  });
  return changed;
}

/**
 * What the summary would be for: null when the query is too short or is only
 * gambits, 'unsupported' on tabs other than Discussions and Posts.
 */
function summaryTarget(modal) {
  const _internals = internals(modal),
    query = _internals.query,
    activeSource = _internals.activeSource;
  const raw = query();
  if (raw.trim().length < SearchManager.minSearchLength()) return null;
  if (!SUMMARY_RESOURCES.includes(activeSource().resource)) return 'unsupported';

  // The backend summarises discussions, whichever tab is open. Lowercased
  // like the results it sits above.
  const request = buildRequest('discussions', raw.toLowerCase());
  return request.q ? {
    raw,
    request,
    key: JSON.stringify(request)
  } : null;
}

/**
 * Whether the active tab has results for `raw` yet, and enough of them to be
 * worth summarising.
 */
function resultsReadiness(modal, raw) {
  const _internals2 = internals(modal),
    activeSource = _internals2.activeSource,
    loadingSources = _internals2.loadingSources;
  const source = activeSource();
  if (!source.isCached(raw) || loadingSources.includes(source.resource)) return 'pending';
  return source.view(raw).length >= SUMMARY_MIN_RESULTS ? 'ready' : 'few';
}

/**
 * Split gambits off the query the way the store does for result searches,
 * using the gambits of the resource the backend searches.
 */
function buildRequest(resource, query) {
  var _filter$q;
  const filter = app.search.gambits.apply(resource, {
    q: query.trim()
  });
  const q = String((_filter$q = filter.q) != null ? _filter$q : '');
  delete filter.q;
  return Object.keys(filter).length ? {
    q,
    filter
  } : {
    q
  };
}
function toggleAsk(modal, state) {
  var _internals$inputEleme2;
  state.askMode = !state.askMode;
  if (state.askMode) submitAsk(modal, state);
  (_internals$inputEleme2 = internals(modal).inputElement()[0]) == null || _internals$inputEleme2.focus();
}
function submitAsk(modal, state) {
  const question = internals(modal).query().trim();
  if (question.length < SearchManager.minSearchLength()) return;

  // The backend answers from posts.
  const request = buildRequest('posts', question);
  if (!request.q) return;
  const key = JSON.stringify(request);
  const ask = state.ask;

  // Enter on the question already answered (or being answered) does nothing.
  if (ask.key === key && !['aborted', 'error', 'interrupted', 'rate_limited'].includes(ask.status)) return;
  ask.start(key, request);
}
function caret() {
  return m("span", {
    className: "GlowingBlueRag-caret",
    "aria-hidden": "true"
  });
}
function loading(text) {
  return m("p", {
    className: "GlowingBlueRag-message"
  }, m(LoadingIndicator, {
    size: "small",
    display: "inline"
  }), " ", text);
}
function summaryMessage(status) {
  switch (status) {
    case 'empty':
      return app.translator.trans('glowingblue-search.forum.summary.empty');
    case 'no_answer':
      return app.translator.trans('glowingblue-search.forum.summary.no_answer');
    case 'error':
      return app.translator.trans('glowingblue-search.forum.summary.error');
    case 'interrupted':
      return app.translator.trans('glowingblue-search.forum.summary.interrupted');
    case 'rate_limited':
      return app.translator.trans('glowingblue-search.forum.ai.rate_limited');
    default:
      return null;
  }
}
function askMessage(status) {
  switch (status) {
    case 'empty':
      return app.translator.trans('glowingblue-search.forum.ask.empty');
    case 'no_answer':
      return app.translator.trans('glowingblue-search.forum.ask.no_answer');
    case 'error':
      return app.translator.trans('glowingblue-search.forum.ask.error');
    case 'interrupted':
      return app.translator.trans('glowingblue-search.forum.ask.interrupted');
    case 'rate_limited':
      return app.translator.trans('glowingblue-search.forum.ai.rate_limited');
    default:
      return null;
  }
}
function measureOverflow(vnode) {
  const el = vnode.dom;
  el.toggleAttribute('data-overflowing', el.scrollHeight - el.clientHeight > 1);
}
function renderSummary(state, target) {
  const summary = state.summary;
  const current = summary.key === target.key && !summary.retained;
  const streaming = current && summary.status === 'streaming';
  const message = summaryMessage(summary.status);
  const cited = !message && summary.text ? renderCitedText(summary.text, summary.sources, streaming, discussionUrl) : null;
  let body;
  if (message) {
    body = m("p", {
      className: "GlowingBlueRag-message"
    }, message);
  } else if (cited) {
    body = m("p", {
      className: "GlowingBlueRag-summary-text",
      oncreate: measureOverflow,
      onupdate: measureOverflow
    }, cited.children, streaming && caret());
  } else {
    body = loading(app.translator.trans('glowingblue-search.forum.summary.loading'));
  }
  return m("section", {
    className: classList('GlowingBlueRag-summary', {
      'GlowingBlueRag-summary--stale': !current && (!!message || !!cited),
      'GlowingBlueRag-summary--expanded': state.summaryExpanded
    })
  }, m("div", {
    className: "GlowingBlueRag-summary-header"
  }, m(Icon, {
    name: "fas fa-wand-magic-sparkles"
  }), m("span", null, app.translator.trans('glowingblue-search.forum.summary.header'))), m("div", {
    className: "GlowingBlueRag-summary-body",
    role: "status",
    "aria-live": "polite",
    "aria-busy": !current || summary.isRunning() ? 'true' : 'false'
  }, body), !!cited && m(Button, {
    className: "Button Button--link GlowingBlueRag-summary-more",
    "aria-expanded": state.summaryExpanded ? 'true' : 'false',
    onclick: () => state.summaryExpanded = !state.summaryExpanded
  }, state.summaryExpanded ? app.translator.trans('glowingblue-search.forum.summary.show_less') : app.translator.trans('glowingblue-search.forum.summary.show_more')));
}
function renderAskToggle(modal, state) {
  return m("div", {
    className: "GlowingBlueRag-askToggle"
  }, m(Button, {
    className: "Button Button--flat GlowingBlueRag-askToggle-btn",
    "aria-pressed": state.askMode ? 'true' : 'false',
    onclick: () => toggleAsk(modal, state)
  }, m(Icon, {
    name: "fas fa-wand-magic-sparkles"
  }), " ", app.translator.trans('glowingblue-search.forum.ask.toggle')));
}
function setAskPlaceholder(form) {
  if (!form || typeof form !== 'object' || Array.isArray(form)) return;
  const children = form.children;
  if (!Array.isArray(children)) return;
  const input = children.find(child => {
    var _attrs;
    return (child == null || (_attrs = child.attrs) == null || (_attrs = _attrs.inputAttrs) == null ? void 0 : _attrs.className) === 'SearchModal-input';
  });
  if (!input) return;
  const label = extractText(app.translator.trans('glowingblue-search.forum.ask.placeholder'));
  input.attrs.placeholder = label;
  input.attrs['aria-label'] = label;
}
function renderAskPanel(modal, state) {
  const ask = state.ask;
  const streaming = ask.status === 'streaming';
  const message = askMessage(ask.status);
  const answer = !message && ask.text ? renderCitedText(ask.text, ask.sources, streaming, postUrl) : null;
  let body;
  if (message) {
    body = m("p", {
      className: "GlowingBlueRag-message"
    }, message);
  } else if (answer) {
    body = m("div", {
      className: "GlowingBlueRag-ask-answer"
    }, answer.children, streaming && caret());
  } else if (ask.status === 'waiting') {
    body = loading(app.translator.trans('glowingblue-search.forum.ask.loading'));
  } else {
    body = m("p", {
      className: "GlowingBlueRag-message"
    }, app.translator.trans('glowingblue-search.forum.ask.prompt'));
  }
  return m("div", {
    className: "GlowingBlueRag-ask",
    onclick: e => internals(modal).onLinkClick(e)
  }, m("div", {
    className: "GlowingBlueRag-ask-header"
  }, m(Icon, {
    name: "fas fa-wand-magic-sparkles"
  }), m("span", null, app.translator.trans('glowingblue-search.forum.ask.header'))), m("div", {
    className: "GlowingBlueRag-ask-body",
    role: "status",
    "aria-live": "polite",
    "aria-busy": ask.isRunning() ? 'true' : 'false'
  }, body), !!answer && answer.citations.length > 0 && renderSources(answer.citations), !!answer && m("p", {
    className: "GlowingBlueRag-ask-disclaimer"
  }, app.translator.trans('glowingblue-search.forum.ask.disclaimer')));
}

/**
 * The cited sources, one entry per discussion in order of first citation,
 * each with the citations that point into it.
 */
function renderSources(citations) {
  const groups = new Map();
  for (const citation of citations) {
    var _groups$get;
    const id = Number(citation.source.discussion_id);
    groups.set(id, [...((_groups$get = groups.get(id)) != null ? _groups$get : []), citation]);
  }
  return m("div", {
    className: "GlowingBlueRag-sources"
  }, m("h4", {
    className: "GlowingBlueRag-sources-heading"
  }, app.translator.trans('glowingblue-search.forum.ask.sources')), m("ul", {
    className: "GlowingBlueRag-sources-list"
  }, Array.from(groups, _ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      id = _ref2[0],
      group = _ref2[1];
    return m("li", {
      key: id,
      className: "GlowingBlueRag-source"
    }, m("div", {
      className: "GlowingBlueRag-source-head"
    }, m(Link, {
      href: postUrl(group[0].source),
      className: "GlowingBlueRag-source-title"
    }, sourceTitle(group[0].source)), m("span", {
      className: "GlowingBlueRag-source-refs"
    }, group.map(citation => citationLink(citation.label, citation.source, postUrl(citation.source))))), group.filter(citation => citation.source.excerpt).map(citation => m("details", {
      key: citation.label,
      className: "GlowingBlueRag-source-excerpt"
    }, m("summary", null, group.length > 1 ? app.translator.trans('glowingblue-search.forum.ask.excerpt_numbered', {
      n: citation.label
    }) : app.translator.trans('glowingblue-search.forum.ask.excerpt')), m("p", null, citation.source.excerpt))));
  })));
}